import { useReducer, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Star,
  Clock,
  Shield,
  Play,
  Dna,
  Brain,
  HelpCircle,
  ChevronLeft,
  X,
} from 'lucide-react';
import { MMEMParams } from '../types';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface NeuralQuestProps {
  params: MMEMParams;
  onBack?: () => void;
}

type GameState = 'idle' | 'showing' | 'playing' | 'failed' | 'success';

interface State {
  sequence: number[];
  userSequence: number[];
  gameState: GameState;
  score: number;
  level: number;
  xp: number;
  activeNode: number | null;
  boosters: {
    slowMo: number;
    shield: number;
  };
  isSlowMo: boolean;
  showInstructions: boolean;
}

type Action =
  | { type: 'START_SHOWING'; payload: { sequence: number[] } }
  | { type: 'FINISH_SHOWING' }
  | { type: 'SET_ACTIVE_NODE'; payload: number | null }
  | { type: 'NODE_CLICK'; payload: number }
  | { type: 'RESET_GAME' }
  | { type: 'USE_SLOW_MO' }
  | { type: 'TOGGLE_INSTRUCTIONS' };

const initialState: State = {
  sequence: [],
  userSequence: [],
  gameState: 'idle',
  score: 0,
  level: 1,
  xp: 0,
  activeNode: null,
  boosters: { slowMo: 1, shield: 0 },
  isSlowMo: false,
  showInstructions: false,
};

function gameReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START_SHOWING':
      return {
        ...state,
        sequence: action.payload.sequence,
        userSequence: [],
        gameState: 'showing',
      };

    case 'FINISH_SHOWING':
      return {
        ...state,
        gameState: 'playing',
        isSlowMo: false, // Reset slow-mo after sequence is shown
      };

    case 'SET_ACTIVE_NODE':
      return {
        ...state,
        activeNode: action.payload,
      };

    case 'NODE_CLICK': {
      if (state.gameState !== 'playing') {
        return state;
      }

      const newUserSequence = [...state.userSequence, action.payload];
      const currentIndex = newUserSequence.length - 1;

      // Check for mismatch
      if (newUserSequence[currentIndex] !== state.sequence[currentIndex]) {
        // Use shield if available
        if (state.boosters.shield > 0) {
          return {
            ...state,
            boosters: { ...state.boosters, shield: state.boosters.shield - 1 },
          };
        }
        // Otherwise, fail
        return { ...state, gameState: 'failed' };
      }

      // If sequence is now complete and correct
      if (newUserSequence.length === state.sequence.length) {
        const gainedXp = state.sequence.length * 10;
        const newTotalXp = state.xp + gainedXp;
        const nextLevelXp = state.level * 100;

        // Check for level up
        if (newTotalXp >= nextLevelXp) {
          return {
            ...state,
            gameState: 'success',
            userSequence: newUserSequence,
            score: state.score + state.sequence.length,
            level: state.level + 1,
            xp: newTotalXp - nextLevelXp,
            boosters: {
              slowMo: state.boosters.slowMo + 1,
              shield: state.boosters.shield + (state.level % 2 === 0 ? 1 : 0),
            },
          };
        }

        // Just gain XP
        return {
          ...state,
          gameState: 'success',
          userSequence: newUserSequence,
          score: state.score + state.sequence.length,
          xp: newTotalXp,
        };
      }

      // Correct click, continue playing
      return { ...state, userSequence: newUserSequence };
    }

    case 'RESET_GAME':
      return {
        ...initialState,
        // Persist progress and settings
        boosters: state.boosters,
        level: state.level,
        score: state.score,
        xp: state.xp,
        showInstructions: state.showInstructions,
      };

    case 'USE_SLOW_MO':
      if (state.boosters.slowMo > 0 && state.gameState === 'idle') {
        return {
          ...state,
          boosters: { ...state.boosters, slowMo: state.boosters.slowMo - 1 },
          isSlowMo: true,
        };
      }
      return state;

    case 'TOGGLE_INSTRUCTIONS':
      return {
        ...state,
        showInstructions: !state.showInstructions,
      };

    default:
      return state;
  }
}

// --- Audio Cues ---
// NOTE: You will need to add 9 audio files (e.g., note1.mp3, note2.mp3, etc.)
// to the `public/audio/` directory for these sounds to work.
const playNodeSound = (index: number) => {
  // Create a new Audio object to allow for overlapping sounds.
  // We use modulo 9 because we have 9 sound files, which will be reused for the 4x4 grid.
  const audio = new Audio(`/audio/note${(index % 9) + 1}.mp3`);
  audio.volume = 0.7; // Adjust volume as needed
  audio.play().catch((error) => console.error('Error playing sound:', error));
};

export default function NeuralQuest({ params, onBack }: NeuralQuestProps) {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const [gridSize, setGridSize] = useState(3);
  const [userActiveNode, setUserActiveNode] = useState<number | null>(null);
  const numNodes = gridSize * gridSize;
  const gridColMap = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };
  const {
    gameState,
    score,
    level,
    xp,
    activeNode,
    boosters,
    showInstructions,
  } = state;

  const nextLevelXp = level * 100;

  // Difficulty is derived from MMEM params and current level
  const sequenceLength = Math.max(
    3,
    Math.floor((params.attention * params.mood) / 15) + Math.floor(level / 2)
  );
  const baseDisplayTime = Math.max(300, 1000 - params.sleep * 50);
  const displayTime = state.isSlowMo ? baseDisplayTime * 2 : baseDisplayTime;

  const startGame = async () => {
    const newSequence = Array.from({ length: sequenceLength }, () =>
      Math.floor(Math.random() * numNodes)
    );
    dispatch({ type: 'START_SHOWING', payload: { sequence: newSequence } });

    for (let i = 0; i < newSequence.length; i++) {
      dispatch({ type: 'SET_ACTIVE_NODE', payload: newSequence[i] });
      playNodeSound(newSequence[i]);
      await new Promise((resolve) => setTimeout(resolve, displayTime));
      dispatch({ type: 'SET_ACTIVE_NODE', payload: null });
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    dispatch({ type: 'FINISH_SHOWING' });
  };

  const handleNodeClick = (index: number) => {
    if (state.gameState === 'playing') {
      playNodeSound(index);
      // Briefly highlight the node the user clicked for feedback
      setUserActiveNode(index);
      setTimeout(() => {
        setUserActiveNode(null);
      }, 150);
    }
    dispatch({ type: 'NODE_CLICK', payload: index });
  };

  useEffect(() => {
    if (gameState === 'success') {
      const timer = setTimeout(startGame, 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState]);

  const useSlowMo = () => {
    if (boosters.slowMo > 0 && gameState === 'idle') {
      dispatch({ type: 'USE_SLOW_MO' });
      startGame();
    }
  };

  const SvgGrid = () => {
    if (gridSize < 2) return null;

    const points = Array.from({ length: numNodes }).map((_, i) => {
      const col = i % gridSize;
      const row = Math.floor(i / gridSize);
      const x = 17 + col * (66 / (gridSize - 1));
      const y = 17 + row * (66 / (gridSize - 1));
      return { x, y };
    });

    const lines = [];
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const currentPoint = points[row * gridSize + col];
        // Horizontal connection
        if (col < gridSize - 1) {
          const rightPoint = points[row * gridSize + col + 1];
          lines.push(
            <line
              key={`h-${row}-${col}`}
              x1={currentPoint.x}
              y1={currentPoint.y}
              x2={rightPoint.x}
              y2={rightPoint.y}
              stroke="currentColor"
              strokeWidth="0.5"
            />
          );
        }
        // Vertical connection
        if (row < gridSize - 1) {
          const bottomPoint = points[(row + 1) * gridSize + col];
          lines.push(
            <line
              key={`v-${row}-${col}`}
              x1={currentPoint.x}
              y1={currentPoint.y}
              x2={bottomPoint.x}
              y2={bottomPoint.y}
              stroke="currentColor"
              strokeWidth="0.5"
            />
          );
        }
      }
    }
    return (
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-20"
        viewBox="0 0 100 100"
      >
        {lines}
      </svg>
    );
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col items-center gap-1 p-2 sm:gap-2 sm:p-4 md:gap-4">
      {/* Header with Back button and Instructions toggle */}
      <div className="flex w-full items-center justify-between">
        {onBack && (
          <button
            onClick={onBack}
            className="hover:text-primary flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            View Analytics
          </button>
        )}
        <button
          onClick={() => dispatch({ type: 'TOGGLE_INSTRUCTIONS' })}
          className="text-primary hover:text-primary-dark flex items-center gap-2 text-sm font-bold transition-colors"
        >
          <HelpCircle className="h-4 w-4" />
          {showInstructions ? 'Hide Instructions' : 'How to Play'}
        </button>
      </div>

      {/* Instructions Panel */}
      {showInstructions && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        >
          <div className="bg-surface border-border-main relative w-full max-w-2xl rounded-3xl border p-6">
            <button
              onClick={() => dispatch({ type: 'TOGGLE_INSTRUCTIONS' })}
              className="absolute top-4 right-4 text-slate-400 transition-colors hover:text-slate-600"
            >
              <X className="h-6 w-6" />
            </button>
            <h4 className="text-primary mb-4 flex items-center gap-2 text-lg font-black">
              <Brain className="h-5 w-5" />
              Neural Quest Instructions
            </h4>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                    1
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Watch the <strong>Neural Sequence</strong> as nodes light up
                    one by one.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                    2
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Rehearse</strong> the memory by clicking the nodes
                    in the exact same order.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                    3
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Consolidate links to earn <strong>XP</strong> and level up
                    your neural capacity.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                    4
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Listen for <strong>Audio Cues</strong> to engage your
                    auditory memory pathways.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-indigo-500">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs font-bold">
                    Slow-Mo: Doubles encoding time for easier tracking.
                  </span>
                </div>
                <div className="flex items-center gap-3 text-emerald-500">
                  <Shield className="h-4 w-4" />
                  <span className="text-xs font-bold">
                    Shield: Protects against one synaptic mismatch.
                  </span>
                </div>
                <div className="border-border-main mt-4 rounded-xl border bg-slate-100 p-3 dark:bg-slate-800/50">
                  <p className="text-[10px] leading-relaxed font-bold text-slate-500">
                    <strong>PRO TIP:</strong> Your current MMEM parameters
                    affect difficulty. High{' '}
                    <strong>Attention ({params.attention}/10)</strong> increases
                    complexity ({sequenceLength} nodes), while{' '}
                    <strong>Sleep ({params.sleep}/10)</strong> improves
                    stability ({Math.round(displayTime)}ms).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* HUD */}
      <div className="bg-surface/50 border-border-main flex w-full items-center justify-between rounded-2xl border p-3 shadow-xl backdrop-blur-md sm:p-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 border-primary/20 flex h-10 w-10 items-center justify-center rounded-xl border sm:h-12 sm:w-12 sm:rounded-2xl">
            <Star className="text-primary h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div>
            <div className="text-xs font-black tracking-widest text-slate-400 uppercase">
              Level {level}
            </div>
            <div className="mt-2 h-2 w-32 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(xp / nextLevelXp) * 100}%` }}
                className="bg-primary h-full"
              />
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-black tracking-widest text-slate-400 uppercase">
            Score
          </div>
          <div className="text-text-main text-2xl font-black tracking-tighter tabular-nums sm:text-3xl">
            {score}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center space-y-1 text-center">
        <h3 className="text-text-main text-2xl font-black tracking-tight sm:text-4xl">
          Neural Quest
        </h3>
        <p
          className={cn(
            'text-xs font-black tracking-[0.3em] uppercase',
            gameState === 'failed' ? 'text-rose-500' : 'text-slate-400'
          )}
        >
          {gameState === 'idle'
            ? 'Ready to encode?'
            : gameState === 'showing'
              ? 'Encoding sequence...'
              : gameState === 'playing'
                ? 'Rehearsing memory...'
                : gameState === 'failed'
                  ? 'Synaptic Mismatch'
                  : 'Consolidation Success!'}
        </p>
      </div>

      {/* Difficulty Selector */}
      {gameState === 'idle' && (
        <div className="mb-1 flex justify-center gap-1 rounded-full bg-slate-100 p-1 sm:mb-2 sm:gap-2 sm:p-1.5 dark:bg-slate-800">
          {[2, 3, 4].map((size) => (
            <button
              key={size}
              onClick={() => setGridSize(size)}
              className={cn(
                'rounded-full px-4 py-1.5 text-xs font-bold transition-all',
                gridSize === size
                  ? 'text-primary bg-white shadow-sm dark:bg-slate-700'
                  : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white'
              )}
            >
              {size}x{size}
            </button>
          ))}
        </div>
      )}

      <div
        className={cn(
          'relative grid aspect-square w-full max-w-[min(100%,40vh)] gap-2 md:max-w-md md:gap-4',
          gridColMap[gridSize]
        )}
      >
        <SvgGrid />

        {Array.from({ length: numNodes }).map((_, i) => (
          <motion.button
            key={i}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleNodeClick(i)}
            className={cn(
              'group relative flex items-center justify-center overflow-hidden rounded-2xl border-2 transition-all duration-300 md:rounded-3xl',
              activeNode === i || userActiveNode === i
                ? 'bg-primary border-primary shadow-primary/50 scale-105 shadow-2xl'
                : 'bg-surface border-border-main',
              gameState === 'playing' &&
                'hover:border-primary/50 cursor-pointer'
            )}
          >
            <div
              className={cn(
                'h-8 w-8 rounded-full transition-all duration-300',
                activeNode === i || userActiveNode === i
                  ? 'scale-125 bg-white'
                  : 'bg-slate-200 group-hover:scale-110 dark:bg-slate-700'
              )}
            />
            {activeNode === i && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: displayTime / 1000 }}
                className="bg-primary/20 absolute inset-0"
              />
            )}
          </motion.button>
        ))}
      </div>

      <div className="flex-grow" />

      <div className="w-full max-w-sm flex-shrink-0 space-y-2 sm:space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={useSlowMo}
            disabled={boosters.slowMo === 0 || gameState !== 'idle'}
            className={cn(
              'flex flex-col items-center gap-1 rounded-2xl border p-2 transition-all sm:gap-2 sm:p-4',
              boosters.slowMo > 0 && gameState === 'idle'
                ? 'border-indigo-500/20 bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500/20'
                : 'cursor-not-allowed border-transparent bg-slate-100 text-slate-400 opacity-50 dark:bg-slate-800'
            )}
          >
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <span className="text-sm font-black tracking-widest uppercase">
                Slow-Mo
              </span>
            </div>
            <span className="text-xs font-bold">
              {boosters.slowMo} available
            </span>
          </button>
          <div
            className={cn(
              'flex flex-col items-center gap-1 rounded-2xl border p-2 transition-all sm:gap-2 sm:p-4',
              boosters.shield > 0
                ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500'
                : 'border-transparent bg-slate-100 text-slate-400 opacity-50 dark:bg-slate-800'
            )}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="text-sm font-black tracking-widest uppercase">
                Shield
              </span>
            </div>
            <span className="text-xs font-bold">{boosters.shield} active</span>
          </div>
        </div>
        <div className="flex w-full items-center justify-center">
          {gameState === 'idle' || gameState === 'failed' ? (
            <button
              onClick={
                gameState === 'failed'
                  ? () => dispatch({ type: 'RESET_GAME' })
                  : startGame
              }
              className="bg-primary hover:bg-primary-dark shadow-primary/30 group flex w-full items-center justify-center gap-4 rounded-2xl py-3 font-black text-white shadow-2xl transition-all sm:rounded-3xl sm:py-4"
            >
              <Play className="h-5 w-5 fill-current transition-transform group-hover:scale-110 sm:h-6 sm:w-6" />
              <span className="text-lg tracking-tight sm:text-xl">
                {gameState === 'failed'
                  ? 'Re-Initialize Link'
                  : 'Initialize Link'}
              </span>
            </button>
          ) : (
            <div className="text-primary bg-primary/5 border-primary/10 flex h-[52px] w-full animate-pulse items-center justify-center gap-4 rounded-2xl border font-black sm:h-[64px] sm:rounded-3xl">
              <Dna className="h-6 w-6 animate-spin" />
              <span className="text-base tracking-widest uppercase">
                Neural Sync Active
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
